/**
 * App_Floodgate.ts
 *
 * The entry point for floodgate.
 */

import * as CommonApp from "./CommonApp";
import * as CommonFloodgate from "./CommonFloodgate";
import * as CommonUI from "./CommonUI";
import * as UI from "./UI/UI";
import * as Configuration from "./Configuration/Configuration";
import * as Logging from "./Logging/Logging";
import * as Window from "./Window/Window";
import CustomUISurvey from "./FloodgateCore/UISurvey/CustomUISurvey";
import DummyUISurvey from "./FloodgateCore/UISurvey/DummyUISurvey";
import FloodgateTelemetryLogger from "./FloodgateCore/FloodgateTelemetryLogger";
import ICustomSurvey from "./FloodgateCore/ICustomSurvey";
import IUISurvey from "./FloodgateCore/UISurvey/IUISurvey";

import { FloodgateEngine } from "@ms-ofb/officefloodgatecore";
import { IFloodgateSettingIdMap, ISurvey, ISurveyForm } from "@ms-ofb/officefloodgatecore/dist/src/Api/Api";
import { TelemetryEvent } from "@ms-ofb/officefloodgatecore/dist/src/Constants";
import { customerVoiceLauncherType } from "./Constants";
import { IContextParams, IRenderSurvey, IURLParams, ISurveyOptionalConfigParams } from "./IRenderSurvey";
import { IHandleSurveyEvent } from "./IHandleSurveyEvent";
import { renderCVSurvey } from "gatecrash";
import { handleSurveyEvent } from "./UI/Toast/CVToast";
import { IOverrideRenderCallback } from "./IOverrideRenderCallback";
import UISurvey from "./FloodgateCore/UISurvey/UISurvey";
import { loadStylesheet } from "./Window/DomUtils";

export { getEngineStartPromise, resetEngine, setEngineStartPromise, start, startInternal, stop }
	from "./CommonFloodgate"

/**
 * Has the control been opened. Prevents more than one dialog being generated.
 */
let opened: boolean = false;

/**
 * Initialize. Must be called before any other call to floodgate.
 * @returns A promise which will be rejected if the call fails.
 */
export function initialize(): Promise<any> {
	return new Promise((resolve, reject) => {
		const rejectAndLogError = (errorMessage: string) => {
			const telemetryLogger = FloodgateEngine.getTelemetryLogger();
			if (telemetryLogger) {
				telemetryLogger.log_Event(TelemetryEvent.Floodgate.Initialize.Failed, { ErrorMessage: errorMessage });
			}

			reject(errorMessage);
		};

		if (!CommonFloodgate.getInitialized()) {
			try {
				CommonApp.initialize();
			} catch (err) {
				reject("CommonApp initialization failed: {" + err + "}");
			}

			FloodgateEngine.setTelemetryLogger(new FloodgateTelemetryLogger());

			CommonUI.initialize()
				.then(
					function onInitializeFulfilled() {
						try {
							CommonFloodgate.initialize();
						} catch (err) {
							rejectAndLogError("CommonFloodgate initialization failed: {" + err + "}");
						}

						resolve(null);
					}
				)
				.catch(
					function onInitializeRejected(err) {
						rejectAndLogError("Initialization failed: {" + err + "}");
					}
				);
		} else {
			resolve(null);
		}
	});
}

/**
 * Display the given Survey with it's prompt
 * @param survey the survey to show
 * @param overrideUXRenderCallbackAsync a callback to override rendering
 * (currently only supports CV surveys)
 * @returns A promise which will be rejected if the call fails.
 */
export function showSurvey(
	survey: IUISurvey,
	overrideUXRenderCallbackAsync?: IOverrideRenderCallback
): Promise<void> {
	return new Promise((resolve, reject) => {
		if (opened) {
			reject("Control already open");
			return;
		}

		if (!Configuration.get().isFeedbackSurveyEnabledByAADC()) {
			Logging.getLogger().logEvent(Logging.EventIds.SURVEY_UI_DISABLED_AADC,
				Logging.LogLevel.Error,
				{
					CampaignId: survey.getCampaignId(),
					Data: JSON.stringify({
						ageGroup: Configuration.get().getCommonInitOptions().ageGroup,
						authenticationType: Configuration.get().getCommonInitOptions().authenticationType,
					}),
				});
			reject("Prompt is prohibited by AADC");
			return;
		}

		Configuration.get().setFloodgateSurvey(survey ? survey : new DummyUISurvey());
		const isCVSurveyEnabled = Configuration.get().isCVSurveyEnabled();
		/**
		 * Branching logic to determine if Customer Voice survey is shown to user.
		 */
		if (isCVSurveyEnabled && survey.getLauncherType() === customerVoiceLauncherType) {
			const contextParams: IContextParams = getContextParams(survey);
			const optionalConfigParams: ISurveyOptionalConfigParams = getOptionalConfigParams();

			UI.createCVSurvey(
				contextParams,
				function () { opened = false; },
				optionalConfigParams,
				overrideUXRenderCallbackAsync
			);
			opened = true;
			resolve();
			return;
		}
		UI.createSurvey(function () { opened = false; });
		opened = true;
		resolve();
	});
}

/**
 * Method to create the context params object
 * @param survey the survey being shown
 */
function getContextParams(survey: IUISurvey): IContextParams {
	const urlParams: IURLParams = {};
	if (Configuration.get().getCommonInitOptions().primaryColour) {
		urlParams.appPrimaryColor = Configuration.get().getCommonInitOptions().primaryColour.replace("#", "");
	}
	if (Configuration.get().getCommonInitOptions().secondaryColour) {
		urlParams.appSecondaryColor = Configuration.get().getCommonInitOptions().secondaryColour.replace("#", "");
	}

	return {
		customerVoiceFormId: survey.getCVSurveyFormId(),
		flights: Configuration.get().getCVSurveyFlights(),
		isCommercialHost: Configuration.get().getCommonInitOptions().isCommercialHost,
		locale: Configuration.get().getCommonInitOptions().locale,
		platform: Configuration.get().getCommonInitOptions().telemetryGroup.platform,
		showUserEmailAddress: Configuration.get().getFloodgateInitOptions().showEmailAddress,
		productName: Configuration.get().getProductName(),
		uiHost: Configuration.get().getUIHost(),
		uiHostCVSurveyDispatcher: Configuration.get().getUIHostCVSurveyDispatcher(),
		stylesUrl: Configuration.get().getCommonInitOptions().stylesUrl,
		urlParams,
	};
}

/**
 * Method to create the optional config params
 * @param survey the survey being shown
 */
function getOptionalConfigParams(): ISurveyOptionalConfigParams {
	const optionalConfigParams: ISurveyOptionalConfigParams = {
		closeButtonEnabled: true,
		cancelButtonEnabled: true,
		timeOutEnabled: true,
	};
	if (Configuration.get().getCSSPosition()) {
		optionalConfigParams.cssPosition = Configuration.get().getCSSPosition();
	}
	if (Configuration.get().getCSSZIndex()) {
		optionalConfigParams.cssZIndex = Configuration.get().getCSSZIndex();
	}
	if (Configuration.get().getCSSBottom()) {
		optionalConfigParams.cssBottom = Configuration.get().getCSSBottom();
	}
	if (Configuration.get().getCSSRight()) {
		optionalConfigParams.cssRight = Configuration.get().getCSSRight();
	}
	return optionalConfigParams;
}

/**
 * Method to allow users to launch a survey from an ISurveyForm
 * @param survey the survey to show
 * @param overrideUXRenderCallbackAsync a callback to override rendering
 * (currently only supports CV surveys)
 */
export function showFormSurvey(
	survey: ISurveyForm,
	overrideUXRenderCallbackAsync?: IOverrideRenderCallback
): Promise<any> {
	const genericSurvey: ISurvey = survey as ISurvey;

	const formattedSurvey: IUISurvey = new UISurvey(genericSurvey);
	return showSurvey(formattedSurvey, overrideUXRenderCallbackAsync);
}

/**
 * Method to allow users to launch a custom survey directly
 * @param survey the survey to show
 * @returns A promise which will be rejected if the call fails.
 */
export function showCustomSurvey(survey: ICustomSurvey): Promise<any> {
	return showSurvey(new CustomUISurvey(survey));
}

/**
 * Method to allow users to call survey rendering independent from showSurvey().
 * @param parentElement the container in which the survey will be rendered
 * @param onSurveyEvent a handler for all events emitted by survey tools
 * @param contextParams information about the context in which the survey is shown
 * @param surveyOptionalConfigParams additional survey configuration parameters
 */
export const renderSurvey: IRenderSurvey = (
	parentElement: string | HTMLElement,
	onSurveyEvent: IHandleSurveyEvent,
	contextParams: IContextParams,
	surveyOptionalConfigParams?: ISurveyOptionalConfigParams
): void => {
	const isMainContext = Configuration.get().getFloodgateSurvey() ? true : false;
	if (!isMainContext && contextParams.stylesUrl) {
		loadStylesheet(contextParams.stylesUrl);
	}

	// If a CV form ID is provided, we classify this request as a Customer Voice survey
	if (!!contextParams.customerVoiceFormId) {
		renderCVSurvey(
			parentElement,
			onSurveyEvent,
			contextParams,
			surveyOptionalConfigParams,
			isMainContext
		);
	} else {
		const telemetryLogger = FloodgateEngine.getTelemetryLogger();
		if (telemetryLogger) {
			telemetryLogger.log_Event(
				TelemetryEvent.Floodgate.RenderSurvey.Unsupported,
				{ ErrorMessage: `WARN: renderSurvey currently only supports Customer Voice surveys.` }
			);
		}
	}
};

/**
 * Reset the floodgate module. Used in unit tests.
 */
export function reset() {
	CommonApp.reset();
	CommonUI.reset();
	CommonFloodgate.reset();
	opened = false;
}

/**
 * Get a list of setting names and ids
 * @returns the setting names and ids list
 */
export function getSettingIdMap(): IFloodgateSettingIdMap {
	return IFloodgateSettingIdMap.RoamingSettingIdMap;
}

Window.setFloodgateCustomUXHandler({renderSurvey, handleSurveyEvent});
Window.setFloodgateShowSurvey(showSurvey);
Window.setFloodgateShowFormSurvey(showFormSurvey);
Window.setFloodgateShowCustomSurvey(showCustomSurvey);
Window.setFloodgateInitialize(initialize);
Window.getSettingIdMap(getSettingIdMap);
